import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, Lock, AlertTriangle } from 'lucide-react';

interface ForcePasswordChangeProps {
  open: boolean;
  onPasswordChanged: () => void;
}

export function ForcePasswordChange({ open, onPasswordChanged }: ForcePasswordChangeProps) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isValid = 
    newPassword.length >= 8 && 
    newPassword === confirmPassword;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isValid) {
      toast.error('Hesla se neshodují nebo jsou příliš krátká');
      return;
    }

    setIsSubmitting(true);

    try {
      const { error: passwordError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (passwordError) throw passwordError;

      const { error: metadataError } = await supabase.auth.updateUser({
        data: { must_change_password: false },
      });

      if (metadataError) {
        console.error('Failed to update metadata:', metadataError);
      }

      toast.success('Heslo bylo úspěšně změněno');
      onPasswordChanged();
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error('Chyba při změně hesla');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-amber-50 border border-amber-100 rounded-xl">
              <AlertTriangle className="h-5 w-5 text-amber-700" />
            </div>
            <div>
              <DialogTitle className="text-slate-900">Změna hesla</DialogTitle>
              <DialogDescription>
                Pro pokračování je nutné změnit dočasné heslo
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label htmlFor="newPassword">Nové heslo</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Zadejte nové heslo"
                className="pl-10 h-11 border-slate-200 focus-visible:ring-amber-600/30 focus-visible:border-amber-700"
                minLength={8}
                required
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Minimálně 8 znaků
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Potvrzení hesla</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Zopakujte heslo"
                className="pl-10 h-11 border-slate-200 focus-visible:ring-amber-600/30 focus-visible:border-amber-700"
                minLength={8}
                required
              />
            </div>
            {confirmPassword && newPassword !== confirmPassword && (
              <p className="text-xs text-destructive">
                Hesla se neshodují
              </p>
            )}
          </div>

          <Button
            type="submit"
            className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-medium"
            disabled={!isValid || isSubmitting}
          >
            {isSubmitting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Lock className="mr-2 h-4 w-4" />
            )}
            Změnit heslo
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
